/**
 * useZoomPan — forwards zoom/pan commands to the artboard iframe via postMessage.
 *
 * The artboard's BuilderLayout already uses react-zoom-pan-pinch internally.
 * This hook sends ZOOM_IN / ZOOM_OUT / CENTER_VIEW / RESET_VIEW messages
 * and tracks display zoom purely for the toolbar percentage label.
 */
import { useCallback, useState } from "react";

import { useBuilderStore } from "@/client/stores/builder";

const ZOOM_STEP = 0.2;

export const useZoomPan = () => {
  const [zoom, setZoom] = useState(0.8);
  const [fullscreen, setFullscreen] = useState(false);

  const sendToArtboard = useCallback((type: string) => {
    const frame = useBuilderStore.getState().frame.ref;
    if (!frame?.contentWindow) return;
    frame.contentWindow.postMessage({ type }, "*");
  }, []);

  const zoomIn = useCallback(() => {
    sendToArtboard("ZOOM_IN");
    setZoom((z) => Math.min(z + ZOOM_STEP, 2));
  }, [sendToArtboard]);

  const zoomOut = useCallback(() => {
    sendToArtboard("ZOOM_OUT");
    setZoom((z) => Math.max(z - ZOOM_STEP, 0.4));
  }, [sendToArtboard]);

  const zoomFit = useCallback(() => {
    sendToArtboard("RESET_VIEW");
    setZoom(0.8);
  }, [sendToArtboard]);

  const center = useCallback(() => {
    sendToArtboard("CENTER_VIEW");
  }, [sendToArtboard]);

  const toggleFullscreen = useCallback(() => {
    setFullscreen((f) => !f);
  }, []);

  return {
    zoom,
    fullscreen,
    zoomIn,
    zoomOut,
    zoomFit,
    center,
    toggleFullscreen,
  };
};
